import React from "react";
import { useSearchParams  } from "react-router-dom";

const DealerContext = React.createContext()

// Search param name for each inventory source id we track.
const ID_PARAMS = {
	entityId: "e",
	// CVI 2.1 reads are keyed on the Common Org id, not the platform/org-model id CVI 1.0 uses.
	cvi2DealerId: "co",
	vaDealerId: "va",
	iolVrtId: "iol",
	ddcDealerId: "ddc",
	vinDealerId: "vs",
	atcDealerId: "atc",
};

function DealerContextProvider({ children }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [state, setState] = React.useState({
		entityId: null,
		cvi2DealerId: null,
		vaDealerId: null,
		iolVrtId: null,
		ddcDealerId: null,
		vinDealerId: null,
		atcDealerId: null,
		includeSold: false,
		includeInternal: false,
	});

	// Always the params from the latest render, so a setter captured by a
	// long-running async closure (see DealerLookup) still builds on the current URL.
	const latestParams = React.useRef(searchParams);
	latestParams.current = searchParams;
	// Params we have already navigated to but React has not re-rendered us with
	// yet. Successive setters in the same tick must build on this, not on the URL,
	// because every setSearchParams call gets its own copy of the current params
	// and navigates right away -- so without it only the last call's change sticks.
	const pendingParams = React.useRef(null);

	React.useEffect(() => {
		//console.log("Search params update", searchParams);
		pendingParams.current = null;
		setState({
			entityId: searchParams.get('e') ?? null,
			cvi2DealerId: searchParams.get('co') ?? null,
			vaDealerId: searchParams.get('va') ?? null,
			iolVrtId: searchParams.get('iol') ?? null,
			ddcDealerId: searchParams.get('ddc') ?? null,
			vinDealerId: searchParams.get('vs') ?? null,
			atcDealerId: searchParams.get('atc') ?? null,
			includeSold: searchParams.get('sold') === 'true',
			includeInternal: searchParams.get('internal') === 'true',
		});
	}, [searchParams]);

	const applyParams = (mutate) => {
		const next = new URLSearchParams(pendingParams.current ?? latestParams.current);
		mutate(next);
		pendingParams.current = next;
		setSearchParams(next, { replace: true });
	};

	const value = { state }
	// Sets/clears any combination of inventory source ids in one navigation.
	value.setIds = (ids) => applyParams((params) => {
		for (const [name, param] of Object.entries(ID_PARAMS)) {
			if (!(name in ids)) continue;
			if (ids[name]) params.set(param, ids[name]);
			else params.delete(param);
		}
	});
	// Switches to another dealer: every inventory source id is cleared so none
	// carry over from the dealer we were looking at.
	value.setDealer = (dealerId, { vin } = {}) => applyParams((params) => {
		Object.values(ID_PARAMS).forEach((param) => params.delete(param));
		if (dealerId) params.set("dealerId", dealerId);
		else params.delete("dealerId");
		if (vin) params.set("vin", vin);
	});
	value.toggleSold = () => applyParams((params) => {
		if (params.get("sold") === "true") params.delete("sold");
		else params.set("sold", "true");
	});
	value.toggleInternal = () => applyParams((params) => {
		if (params.get("internal") === "true") params.delete("internal");
		else params.set("internal", "true");
	});
	value.setEntity = (entityId) => value.setIds({ entityId });
	value.setCvi2 = (cvi2DealerId) => value.setIds({ cvi2DealerId });
	value.setVa = (vaDealerId) => value.setIds({ vaDealerId });
	value.setIol = (iolVrtId) => value.setIds({ iolVrtId });
	value.setDdc = (ddcDealerId) => value.setIds({ ddcDealerId });
	value.setVin = (vinDealerId) => value.setIds({ vinDealerId });
	value.setAtc = (atcDealerId) => value.setIds({ atcDealerId });
	// NOTE: you *might* need to memoize this value
	// Learn more in http://kcd.im/optimize-context
	return <DealerContext.Provider value={value}>{children}</DealerContext.Provider>
}

function useDealerContext() {
	const context = React.useContext(DealerContext)
	if (context === undefined) {
		throw new Error('useDealerContext must be used within an DealerProvider');
	}
	return context;
}

export { DealerContextProvider, useDealerContext };
